// Bot workspace navigation (hub-ui-step1 spec U4/U5). No imports on purpose: `node --test`
// loads this file directly (ui/tests/botNav.test.ts).

export const BOT_SYSTEMS = ['chatbot', 'chatbot_v2'] as const;
export type BotSystem = (typeof BOT_SYSTEMS)[number];

export const BOT_SECTIONS = ['config', 'knowledge', 'quality', 'intents', 'review', 'moderation', 'tools', 'cache', 'usage', 'forms', 'memory'] as const;
export type BotSection = (typeof BOT_SECTIONS)[number];

export interface BotRef {
  system: BotSystem;
  botId: string;
}

export interface RecentBot extends BotRef {
  name: string | null;
}

export const RECENT_MAX = 8;

export function isBotSystem(v: unknown): v is BotSystem {
  return v === 'chatbot' || v === 'chatbot_v2';
}

export function isBotSection(v: unknown): v is BotSection {
  return typeof v === 'string' && (BOT_SECTIONS as readonly string[]).includes(v);
}

function decode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

export function botPath(ref: BotRef, section: BotSection = 'config'): string {
  return `/bots/${ref.system}/${encodeURIComponent(ref.botId)}/${section}`;
}

/** `/bots/:system/:botId[/:section]` → its parts; legacy and non-bot paths → null. */
export function parseBotPath(pathname: string): (BotRef & { section: BotSection | null }) | null {
  const m = /^\/bots\/([^/]+)\/([^/]+)(?:\/([^/]+))?\/?$/.exec(pathname);
  if (!m) return null;
  const system = decode(m[1]);
  if (!isBotSystem(system)) return null;
  const botId = decode(m[2]).trim();
  if (!botId) return null;
  const section = m[3] === undefined ? null : decode(m[3]);
  return { system, botId, section: isBotSection(section) ? section : null };
}

/** Pre-step-1 URLs were `/bots/:botId/:section` and always meant v1. */
export function legacyBotPath(botId: string, section: BotSection): string {
  return botPath({ system: 'chatbot', botId }, section);
}

export function botLabel(b: { name: string | null; botId: string }): string {
  return b.name?.trim() || b.botId;
}

export function pushRecent(list: RecentBot[], bot: RecentBot, max = RECENT_MAX): RecentBot[] {
  return [bot, ...list.filter((b) => !(b.system === bot.system && b.botId === bot.botId))].slice(0, max);
}

export function parseRecent(raw: string | null): RecentBot[] {
  let v: unknown;
  try {
    v = JSON.parse(raw ?? '[]');
  } catch {
    return [];
  }
  if (!Array.isArray(v)) return [];
  const out: RecentBot[] = [];
  for (const x of v) {
    const o = x as Record<string, unknown> | null;
    if (!o || !isBotSystem(o.system) || typeof o.botId !== 'string' || !o.botId) continue;
    out.push({ system: o.system, botId: o.botId, name: typeof o.name === 'string' ? o.name : null });
  }
  return out.slice(0, RECENT_MAX);
}

/** Keyboard highlight: wraps both ways; -1 when the list is empty. */
export function moveActive(index: number, delta: number, length: number): number {
  if (length <= 0) return -1;
  if (index < 0) return delta > 0 ? 0 : length - 1;
  return (((index + delta) % length) + length) % length;
}

/** A search result is only usable for the query it answered (a stale list must never be chosen). */
export function freshFor<T extends { q: string }>(result: T | null, q: string): T | null {
  return result && result.q === q.trim() ? result : null;
}
